import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Observable, Subscription, take } from 'rxjs';
import { trustBlobResourceUrl } from 'src/app/shared/utils/dom-sanitizer.util';

const NO_USAGE_MESSAGE = 'No pending usage for this period.';
const OFFLINE_MESSAGE = 'Cannot reach the server. Check your connection and try again.';
const FALLBACK_MESSAGE = 'Could not load the preview. Please try again.';

/** In-app PDF viewer: loads a blob, shows it in an iframe, lets the operator download it. */
@Component({
  selector: 'app-pdf-preview-modal',
  standalone: true,
  template: `
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      (click)="onBackdrop($event)"
      data-testid="pdf-backdrop"
    >
      <div
        #dialog
        role="dialog"
        aria-modal="true"
        aria-labelledby="pdf-preview-title"
        class="flex h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-[#0e1726]"
      >
        <div class="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-[#191e3a]">
          <h2 id="pdf-preview-title" class="truncate text-sm font-bold text-black dark:text-white">{{ title }}</h2>
          <div class="flex items-center gap-2">
            <button
              type="button"
              class="rounded-lg border border-primary/40 px-3 py-1 text-xs text-primary disabled:opacity-50"
              [disabled]="!blob"
              (click)="download()"
            >
              Download
            </button>
            <button
              #closeButton
              type="button"
              class="rounded-lg border border-gray-300 px-3 py-1 text-xs dark:border-white/20"
              (click)="close()"
            >
              Close
            </button>
          </div>
        </div>
        <div class="min-h-0 flex-1 bg-gray-100 dark:bg-black/30">
          @if (loading) {
            <p class="p-6 text-center text-sm text-gray-500 dark:text-gray-400">Preparing preview…</p>
          } @else if (error) {
            <p role="alert" class="p-6 text-center text-sm text-danger">{{ error }}</p>
          } @else if (safeUrl) {
            <iframe [src]="safeUrl" title="PDF preview" class="block h-full w-full border-0"></iframe>
          }
        </div>
      </div>
    </div>
  `,
})
export class PdfPreviewModalComponent implements OnInit, OnDestroy {
  @Input({ required: true }) title!: string;
  @Input({ required: true }) filename!: string;
  @Input({ required: true }) load!: () => Observable<Blob>;
  @Output() closed = new EventEmitter<void>();

  @ViewChild('closeButton', { static: true }) closeButton!: ElementRef<HTMLButtonElement>;
  @ViewChild('dialog', { static: true }) dialog!: ElementRef<HTMLElement>;

  private readonly sanitizer = inject(DomSanitizer);
  private subscription?: Subscription;
  private objectUrl: string | null = null;
  private previouslyFocused: HTMLElement | null = null;

  loading = true;
  error = '';
  blob: Blob | null = null;
  safeUrl: SafeResourceUrl | null = null;

  ngOnInit(): void {
    this.previouslyFocused = document.activeElement as HTMLElement | null;
    this.closeButton.nativeElement.focus();
    this.subscription = this.load().pipe(take(1)).subscribe({
      next: (blob) => {
        this.blob = blob;
        this.objectUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        this.safeUrl = trustBlobResourceUrl(this.sanitizer, this.objectUrl);
        this.loading = false;
      },
      error: (err: unknown) => {
        void this.readError(err).then((message) => {
          this.error = message;
          this.loading = false;
        });
      },
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.previouslyFocused?.focus?.();
  }

  close(): void {
    this.closed.emit();
  }

  onBackdrop(event: MouseEvent): void {
    if (event.target === event.currentTarget) this.close();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }

  /** Keeps Tab inside the dialog while it is open. Listens to every keydown because Angular's `keydown.tab` filter skips Shift+Tab. */
  @HostListener('document:keydown', ['$event'])
  trapFocus(event: KeyboardEvent): void {
    if (event.key !== 'Tab') return;
    const items = Array.from(this.dialog.nativeElement.querySelectorAll<HTMLElement>('button:not([disabled]), iframe'));
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /** Reuses the preview URL (revoked on destroy); the anchor must be in the DOM for Firefox/Safari to honour the click. */
  download(): void {
    if (!this.objectUrl) return;
    const link = document.createElement('a');
    link.href = this.objectUrl;
    link.download = this.filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  /** The API answers errors as JSON (404 no usage, 422 validation) even though we asked for a blob; read it so the operator sees the reason. */
  private async readError(err: unknown): Promise<string> {
    if (err instanceof HttpErrorResponse && err.status === 0) return OFFLINE_MESSAGE;
    if (!(err instanceof HttpErrorResponse) || !(err.error instanceof Blob)) return FALLBACK_MESSAGE;
    try {
      const body = JSON.parse(await err.error.text()) as { error?: string; message?: unknown };
      if (body.error === 'no_pending_usage') return NO_USAGE_MESSAGE;
      return this.messageText(body.message) || FALLBACK_MESSAGE;
    } catch {
      return FALLBACK_MESSAGE;
    }
  }

  /** The validation filter sends `message` as { field: [messages] }; Nest's default sends a string or an array. */
  private messageText(message: unknown): string {
    if (typeof message === 'string') return message;
    if (message === null || typeof message !== 'object') return '';
    const parts: unknown[] = Array.isArray(message) ? message : Object.values(message).flat();
    return parts.filter((part): part is string => typeof part === 'string').join(' ');
  }
}
